import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  type OnChanges,
  Output,
  type TemplateRef,
} from '@angular/core';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { type GraphQLResultWithId, type Pageable } from '@reconcept-platform/shared/data-access';
import { LoadingIndicatorComponent } from '@reconcept-platform/shared/ui/core/loading-indicator';
import { ScrollIntoViewDirective } from '@reconcept-platform/shared/ui/core/scroll-into-view';
import { TextLinkActionComponent } from '@reconcept-platform/shared/ui/standalone/button';
import { InfoBoxComponent } from '@reconcept-platform/shared/ui/standalone/info-box';
import { SkeletonTableComponent } from '@reconcept-platform/shared/ui/standalone/skeleton';

@Component({
  selector: 'pageable-table',
  templateUrl: './pageable-table.component.html',
  styleUrls: ['./pageable-table.component.scss'],
  imports: [
    SkeletonTableComponent,
    NgTemplateOutlet,
    ScrollIntoViewDirective,
    LoadingIndicatorComponent,
    TranslatePipe,
    InfoBoxComponent,
    TextLinkActionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PageableTableComponent<T extends GraphQLResultWithId> implements OnChanges {
  @Input() pageable: Pageable<T>;
  @Input() infiniteScroll: boolean;
  @Input() trackRowsBy = 'id';

  @ContentChild('summary')
  summaryTemplateRef: TemplateRef<any>;

  @ContentChild('header')
  headerTemplateRef: TemplateRef<any>;

  @ContentChild('row')
  rowTemplateRef: TemplateRef<any>;

  @ContentChild('noData')
  noDataTemplateRef: TemplateRef<any>;

  @Output() select = new EventEmitter<any>();
  @Output() dropRow = new EventEmitter<any>();

  selected: any[] = [];

  ngOnChanges() {
    if (this.pageable) {
      this.getNextPage();
    }
  }

  getNextPage() {
    this.pageable.getNextPage();
  }

  onScrollIntoView() {
    if (this.infiniteScroll) {
      this.getNextPage();
    }
  }

  toggleCheckAll(value: string) {
    this.selected = value ? [...this.pageable.availableNodes] : [];

    this.select.emit(this.selected);
  }

  toggleCheckRow(selected: string, rowData: any) {
    if (selected) {
      this.selected.push(rowData);
    } else {
      this.selected.splice(this.selected.indexOf(rowData), 1);
    }

    this.select.emit(this.selected);
  }

  protected readonly faAngleDown = faAngleDown;
}
